/**
 * QualificationsList — Learning hub tab. Users can add/edit/delete
 * their own qualifications (name, issuer, issue date, expiry date).
 * Expiry within 30 days shows amber; within 7 days red.
 *
 * Managers see everyone's qualifications with the owner's name.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Award, Plus, Loader2, AlertCircle, Trash2, Edit3, Paperclip, Upload, Download } from "lucide-react";


export default function QualificationsList() {
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | qualification obj
  const [scope, setScope] = useState("mine");   // 'mine' | 'all' (manager only)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = scope === "all" && isManager ? {} : { user_id: user?.id };
      const [rQ, rU] = await Promise.all([
        api.get("/training/qualifications", { params }),
        isManager ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setRows(rQ.data || []);
      setUsers(rU.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [scope, isManager, user?.id]);

  useEffect(() => { load(); }, [load]);

  const userNameById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u.full_name || u.email;
    return m;
  }, [users]);

  async function remove(q) {
    if (!window.confirm(`Delete "${q.name}"?`)) return;
    try {
      await api.delete(`/training/qualifications/${q.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-4" data-testid="qualifications-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-zinc-600">
          Track your professional certifications. Reminders fire 30, 7 and 1
          days before an expiry date.
        </div>
        <div className="flex items-center gap-2">
          {isManager && (
            <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden">
              <button
                onClick={() => setScope("mine")}
                className={`px-3 h-8 text-xs ${scope === "mine" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                data-testid="qualifications-scope-mine"
              >Mine</button>
              <button
                onClick={() => setScope("all")}
                className={`px-3 h-8 text-xs border-l border-zinc-300 ${scope === "all" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                data-testid="qualifications-scope-all"
              >Team</button>
            </div>
          )}
          <Button
            onClick={() => setEditing("new")}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="qualifications-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add qualification
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
          {scope === "all"
            ? "No qualifications recorded yet."
            : "You haven't added any qualifications yet."}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((q) => (
            <QualificationRow
              key={q.id}
              qual={q}
              showOwner={scope === "all"}
              ownerName={userNameById[q.user_id]}
              canEdit={q.user_id === user?.id || isManager}
              onEdit={() => setEditing(q)}
              onDelete={() => remove(q)}
            />
          ))}
        </ul>
      )}

      {editing && (
        <QualificationDialog
          qual={editing === "new" ? null : editing}
          allowUserTarget={isManager}
          users={users}
          currentUserId={user?.id}
          onClose={() => setEditing(null)}
          onSaved={(_doc, opts = {}) => {
            // Refresh list in background so counts stay live.
            load();
            // Keep dialog open when we just saved a NEW qualification —
            // gives the operator an immediate chance to attach a
            // certificate without a second click through Edit.
            if (opts.closeDialog !== false && !opts.silent) setEditing(null);
          }}
        />
      )}
    </div>
  );
}


function QualificationRow({ qual, showOwner, ownerName, canEdit, onEdit, onDelete }) {
  const expiry = qual.expiry_date;
  const daysLeft = useMemo(() => {
    if (!expiry) return null;
    try {
      const now = new Date();
      const e = new Date(`${expiry}T00:00:00Z`);
      return Math.floor((e - now) / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  }, [expiry]);

  const tone = (() => {
    if (daysLeft === null) return "";
    if (daysLeft < 0) return "bg-red-50 text-red-800 border-red-200";
    if (daysLeft <= 7) return "bg-red-50 text-red-800 border-red-200";
    if (daysLeft <= 30) return "bg-amber-50 text-amber-900 border-amber-200";
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  })();

  return (
    <li className="border border-zinc-200 rounded-md p-3" data-testid={`qualification-row-${qual.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-950 flex items-center gap-2 flex-wrap">
            <Award className="h-4 w-4 text-zinc-500" />
            {qual.name}
            {qual.issuer && <span className="text-xs text-zinc-500">· {qual.issuer}</span>}
            {expiry && (
              <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${tone}`}>
                {daysLeft !== null && daysLeft < 0 && <AlertCircle className="h-3 w-3" />}
                {daysLeft === null
                  ? `expires ${expiry}`
                  : daysLeft < 0
                    ? `expired ${-daysLeft}d ago`
                    : `expires in ${daysLeft}d`}
              </span>
            )}
          </div>
          <div className="text-2xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
            {showOwner && ownerName && (
              <span><b className="text-zinc-800">{ownerName}</b></span>
            )}
            {qual.issue_date && <span>issued {qual.issue_date}</span>}
            {qual.expiry_date && <span>· expires {qual.expiry_date}</span>}
          </div>
          {qual.notes && (
            <div className="text-xs text-zinc-600 mt-1">{qual.notes}</div>
          )}
          {(qual.attachments || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5"
                 data-testid={`qualification-attachments-${qual.id}`}>
              {(qual.attachments || []).map((att) => (
                <a
                  key={att.id}
                  href={`/api/training/qualifications/${qual.id}/attachments/${att.id}/download`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-2xs text-zinc-700 hover:text-zinc-950 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5 max-w-[220px] truncate"
                  title={`${att.filename} — click to open`}
                  data-testid={`qualification-att-open-${att.id}`}
                >
                  <Paperclip className="h-3 w-3 shrink-0" />
                  <span className="truncate">{att.filename}</span>
                </a>
              ))}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-md"
              data-testid={`qualification-edit-${qual.id}`}
            >
              <Edit3 className="h-3 w-3" /> Edit
            </button>
            <button
              onClick={onDelete}
              className="text-xs text-red-700 hover:bg-red-50 inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-md"
              data-testid={`qualification-delete-${qual.id}`}
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          </div>
        )}
      </div>
    </li>
  );
}


function QualificationDialog({ qual, allowUserTarget, users, currentUserId, onClose, onSaved }) {
  // Support "add → attach" flow: after the first save, remember the
  // new id so the Attachments section becomes uploadable in the same
  // dialog session.
  const [savedId, setSavedId] = useState(qual?.id || null);
  const [attachments, setAttachments] = useState(qual?.attachments || []);
  const [form, setForm] = useState(() => ({
    name: qual?.name || "",
    issuer: qual?.issuer || "",
    issue_date: qual?.issue_date || "",
    expiry_date: qual?.expiry_date || "",
    notes: qual?.notes || "",
    user_id: qual?.user_id || currentUserId || "",
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function save() {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      let doc;
      if (savedId) {
        const r = await api.patch(`/training/qualifications/${savedId}`, {
          name: form.name.trim(),
          issuer: form.issuer,
          issue_date: form.issue_date || null,
          expiry_date: form.expiry_date || null,
          notes: form.notes,
        });
        doc = r.data;
      } else {
        const r = await api.post("/training/qualifications", {
          name: form.name.trim(),
          issuer: form.issuer,
          issue_date: form.issue_date || null,
          expiry_date: form.expiry_date || null,
          notes: form.notes,
          user_id: allowUserTarget ? form.user_id || undefined : undefined,
        });
        doc = r.data;
        setSavedId(doc.id);
        toast.success("Saved — you can now attach supporting documents");
      }
      if (doc) setAttachments(doc.attachments || []);
      if (savedId) toast.success("Saved");
      onSaved?.(doc, { closeDialog: !!savedId });
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function upload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so same file can be re-picked
    if (!file || !savedId) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(
        `/training/qualifications/${savedId}/attachments`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setAttachments((prev) => [...prev, r.data]);
      toast.success(`Attached ${r.data.filename}`);
      onSaved?.(null, { closeDialog: false, silent: true });
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err.message });
    } finally { setUploading(false); }
  }

  async function removeAttachment(att) {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await api.delete(`/training/qualifications/${savedId}/attachments/${att.id}`);
      setAttachments((prev) => prev.filter((x) => x.id !== att.id));
      toast.success("Attachment removed");
      onSaved?.(null, { closeDialog: false, silent: true });
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || err.message });
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="qualification-dialog">
        <DialogHeader>
          <DialogTitle>{qual ? "Edit qualification" : "Add qualification"}</DialogTitle>
          <DialogDescription className="text-xs">
            The system emails you reminders 30, 7 and 1 days before expiry. Leave
            expiry blank if it doesn&apos;t expire.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="First Aid HLTAID011"
              data-testid="qualification-name"
            />
          </div>
          <div>
            <Label className="text-2xs">Issuer</Label>
            <Input
              value={form.issuer}
              onChange={(e) => setForm({ ...form, issuer: e.target.value })}
              placeholder="St John Ambulance"
              data-testid="qualification-issuer"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Issued</Label>
              <Input
                type="date"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                data-testid="qualification-issue-date"
              />
            </div>
            <div>
              <Label className="text-2xs">Expires</Label>
              <Input
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                data-testid="qualification-expiry-date"
              />
            </div>
          </div>
          {allowUserTarget && !qual && (
            <div>
              <Label className="text-2xs">Owner</Label>
              <select
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                className="mt-1 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white w-full"
                data-testid="qualification-owner"
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Label className="text-2xs">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="qualification-notes"
            />
          </div>

          <div className="border-t border-zinc-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-2xs flex items-center gap-1.5">
                  <Paperclip className="h-3 w-3" /> Supporting documents
                </Label>
                <div className="text-2xs text-zinc-500">
                  Certificate scans, diplomas, evidence of currency. PDF or image up to 25 MB.
                </div>
              </div>
              <label
                className={`inline-flex items-center gap-1 text-xs px-2.5 h-8 rounded-md border cursor-pointer whitespace-nowrap ${
                  savedId
                    ? "border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-900"
                    : "border-zinc-200 bg-zinc-50 text-zinc-400 cursor-not-allowed"
                }`}
                title={savedId ? "Attach a file" : "Save the qualification first, then attach"}
                data-testid="qualification-attach-btn"
              >
                {uploading
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <Upload className="h-3 w-3" />}
                {uploading ? "Uploading…" : "Attach file"}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.doc,.docx,application/pdf,image/*"
                  className="hidden"
                  disabled={!savedId || uploading}
                  onChange={upload}
                  data-testid="qualification-attach-input"
                />
              </label>
            </div>
            {!savedId ? (
              <div className="text-2xs text-zinc-500 italic">
                Save the qualification first — the attachment button unlocks straight after.
              </div>
            ) : attachments.length === 0 ? (
              <div className="text-2xs text-zinc-500 italic border border-dashed border-zinc-200 rounded px-2 py-2">
                No supporting documents yet.
              </div>
            ) : (
              <ul className="space-y-1" data-testid="qualification-att-list">
                {attachments.map((att) => (
                  <li key={att.id}
                      className="flex items-center gap-2 border border-zinc-200 rounded px-2 py-1.5 text-xs bg-white">
                    <Paperclip className="h-3 w-3 text-zinc-500 shrink-0" />
                    <span className="flex-1 truncate" title={att.filename}>{att.filename}</span>
                    <span className="text-2xs text-zinc-500 font-mono-num whitespace-nowrap">
                      {Math.round((att.size_bytes || 0) / 1024)} KB
                    </span>
                    <a
                      href={`/api/training/qualifications/${savedId}/attachments/${att.id}/download`}
                      target="_blank" rel="noreferrer"
                      className="p-1 rounded hover:bg-zinc-100 text-zinc-600"
                      title="Open in new tab"
                      data-testid={`qualification-att-download-${att.id}`}
                    >
                      <Download className="h-3 w-3" />
                    </a>
                    <button
                      onClick={() => removeAttachment(att)}
                      className="p-1 rounded hover:bg-red-50 text-red-600"
                      title="Delete"
                      data-testid={`qualification-att-remove-${att.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {savedId ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-zinc-950 text-white"
            data-testid="qualification-save"
          >
            {saving
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
              : (savedId ? "Save changes" : "Save & attach files")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
